import { useRoutes, Navigate } from "react-router-dom";
import React, { Suspense, lazy } from "react";
import { useSelector } from "react-redux";

// ✅ Make every page lazy-loaded
const AdminPage = lazy(() => import("../Component/Store/AdminPage"));
const StockManagementPage = lazy(() =>
  import("../Component/Store/StockManagement")
);
const Purchase = lazy(() => import("../Component/Store/Purchase/Purchase"));
const Suppliers = lazy(() => import("../Component/Store/Suppliers"));
const Consumption = lazy(() => import("../Component/Store/Consumption"));
const PurchaseReturns = lazy(() =>
  import("../Component/Store/Purchase/PurchaseReturns")
);
const Sales = lazy(() => import("../Component/Store/Sales/Bills/BillEntry"));
const CompanyPage = lazy(() => import("../Component/Partners/CompanyPage"));
const Unauthorized = lazy(() => import("./Unauthorized"));
const Home = lazy(() => import("../Component/Home/Home"));
const Admin = lazy(() => import("../Component/Home/Admin"));
const NaadanMartHome = lazy(() => import("../Component/Home/NaadanmartHome"));
const StorePartnerSignup = lazy(() =>
  import("../Component/Auth/StorePartnerSignup")
);

// Protected Route Component
const ProtectedRoute = ({ element, allowedRights }) => {
  const auth = useSelector((state) => state.authentication);

  const rightsRedux = auth.userData.rights;
  const tempoToken = auth.userData.token;

  if (!tempoToken || tempoToken === "undefined") {
    return <Navigate to="/" replace />;
  }

  const hasPermission = allowedRights.some(
    (right) => rightsRedux && rightsRedux.includes(right)
  );

  return hasPermission ? element : <Navigate to="/unauthorized" replace />;
};

// Loader Component to Wrap Suspense
const SuspenseLoader = ({ component }) => {
  return <Suspense fallback={<div>Loading...</div>}>{component}</Suspense>;
};

// Routing Component
export default function Routing({ userRights, setUserRights }) {
  let elements = useRoutes([
    {
      path: "/",
      element: (
        <SuspenseLoader
          component={<NaadanMartHome setUserRights={setUserRights} />}
        />
      ),
    },

    {
      path: "unauthorized",
      element: <SuspenseLoader component={<Unauthorized />} />,
    },

    {
      path: "home",
      element: <SuspenseLoader component={<Home />} />,
    },

    {
      path: "admin",
      element: (
        <SuspenseLoader
          component={
            <ProtectedRoute element={<Admin />} allowedRights={["Admin"]} />
          }
        />
      ),
    },

    {
      path: "company",
      element: (
        <SuspenseLoader
          component={
            <ProtectedRoute
              element={<CompanyPage />}
              allowedRights={["Admin", "HR", "HR Executive", "Recruiter"]}
            />
          }
        />
      ),
    },
    {
      path: "store-partner-signup",
      element: <SuspenseLoader component={<StorePartnerSignup />} />,
    },

    {
      path: "store-admin",
      element: (
        <SuspenseLoader
          component={
            <ProtectedRoute
              element={<AdminPage />}
              rights={userRights}
              allowedRights={[
                "Admin",
                "Chairman",
                "HR Executive",
                "Recruiter",
                "Principal",
                "AdminExecutive",
                "Accounts",
              ]}
            />
          }
        />
      ),
      children: [
        {
          path: "",
          element: <SuspenseLoader component={<StockManagementPage />} />,
        },
        {
          path: "sales",
          element: <SuspenseLoader component={<Sales />} />,
        },
        {
          path: "sku",
          element: <SuspenseLoader component={<StockManagementPage />} />,
        },
        {
          path: "purchase",
          element: <SuspenseLoader component={<Purchase />} />,
        },
        {
          path: "suppliers",
          element: <SuspenseLoader component={<Suppliers />} />,
        },
        {
          path: "consume",
          element: <SuspenseLoader component={<Consumption />} />,
        },
        {
          path: "rtn-purchase",
          element: <SuspenseLoader component={<PurchaseReturns />} />,
        },
      ],
    },
  ]);

  return elements;
}
